import { API_BASE_URL } from '../config';
import api from './api';

// Base URL for loan-related endpoints
const API_URL = `${API_BASE_URL}/`;
const API_URL_LOANS = `${API_BASE_URL}/loans/`;

export const createLoan = async (loanData) => {
    try {
        console.log('Sende Kreditdaten an API: ', loanData);
        const response = await api.post(API_URL_LOANS, loanData, {
            headers: {
                'Content-Type': 'application/json',
            }
        });
        console.log('API Antwort: ', response.data);
        
        if (response.data && response.data.id) {
            return response.data;
        } else {
            throw new Error('Unerwartete API-Antwort');
        }
    } catch (error) {
        console.error('API Fehler:', error.response?.data || error.message);
        // Throw the actual error data from the API
        if (error.response?.data) {
            throw error.response.data;
        }
        throw new Error('Fehler beim Anlegen des Vorgangs');
    }
};

export const getLoansByFilters = async (filters) => {
    try {
        let url = `${API_URL_LOANS}`;
        
        // Add filters to URL
        const queryParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value) {
                queryParams.append(key, value);
            }
        });
        
        if (queryParams.toString()) {
            url += `?${queryParams.toString()}`;
        }
        
        const response = await api.get(url);
        
        return response.data;
    } catch (error) {
        console.error('Error fetching loans:', error);
        throw error;
    }
};

export const getConsultantBillingLoans = async (consultantId) => {
    try {
        const url = `${API_URL_LOANS}consultant-billing/${consultantId}/`;
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching consultant billing loans:', error);
        return [];
    }
};

export const createOrUpdateCommission = async (loanId, commissionData) => {
    try {
        // Prüfe zuerst, ob eine Commission existiert
        try {
            const response = await api.get(`${API_URL_LOANS}${loanId}/commission/`);

            // Wenn eine Commission existiert, update sie
            if (response.data) {
                return await api.patch(
                    `${API_BASE_URL}/commissions/${response.data.id}/`,
                    commissionData
                );
            }
        } catch (error) {
            // Wenn 404, dann existiert noch keine Commission
            if (error.response?.status !== 404) {
                throw error;
            }
        }

        // Wenn keine Commission existiert oder 404 zurückgegeben wurde, erstelle eine neue
        const newCommissionData = {
            loan_id: loanId,  
            invoice_date: commissionData.invoice_date,
            invoice_number: commissionData.invoice_number
        };

        console.log('Sending commission data:', newCommissionData); // Debug-Log

        return await api.post(
            `${API_BASE_URL}/commissions/`,
            newCommissionData
        );
    } catch (error) {
        console.error('API Error:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
        }
        throw error;
    }
};

export const updateCommissionInvoiceInfo = async (commissionId, invoiceData) => {
    try {
        const response = await api.patch(
            `${API_BASE_URL}/commissions/${commissionId}/update_invoice_info/`,
            invoiceData
        );
        return response.data;
    } catch (error) {
        console.error('API Error:', error);
        console.error('Error response:', error.response?.data);
        throw error;
    }
};

export const getInvoicesByFilters = async (year, consultant = '') => {
    try {
        let url = `${API_BASE_URL}/invoices/?year=${year}`;
        if (consultant) {
            url += `&consultant=${consultant}`;
        }
        
        const response = await api.get(url);
        
        return response.data;
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

export const updateInvoiceStatus = async (invoiceId, status) => {
    try {
        const response = await api.patch(
            `${API_BASE_URL}/invoices/${invoiceId}/`,
            { status }
        );
        return response.data;
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

export const getLoanTypes = async () => {
    try {
        const response = await api.get(
            `${API_BASE_URL}/loan-types/`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching loan types:', error);
        return [];
    }
};

export const getBanks = async () => {
    try {
        const response = await api.get(
            `${API_BASE_URL}/banks/`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching banks:', error);
        return [];
    }
};

export const getPoolers = async () => {
    try {
        const response = await api.get(
            `${API_BASE_URL}/poolers/`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching poolers:', error);
        return [];
    }
};

export const getReferredBy = async () => {
    try {
        const response = await api.get(
            `${API_BASE_URL}/referred-by/`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching referred by:', error);
        return [];
    }
};

export const getReferredByCustomer = async () => {
    try {
        const response = await api.get(
            `${API_BASE_URL}/referred-by-customer/`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching referred by customer:', error);
        return [];
    }
};

export const updateInvoiceData = async (loanId, data) => {
    try {
        let endpoint;
        let cleanedData = {};

        // Bestimme den Endpoint und filtere die relevanten Daten
        switch(data.type) {
            case 'invoice_number':
            case 'statement':
            case 'payment':
            case 'consultant_billing':
                // Normale Rechnungsdaten
                endpoint = 'update_invoice_data';
                // Rechnungsnummer und Datum
                if (data.invoice_date) cleanedData.invoice_date = data.invoice_date;
                if (data.invoice_number) cleanedData.invoice_number = data.invoice_number;
                // Abrechnungsdaten
                if (data.statement_received_date) cleanedData.statement_received_date = data.statement_received_date;
                if (data.statement_received_number) cleanedData.statement_received_number = data.statement_received_number;
                // Zahlungsdatum
                if (data.payment_date) cleanedData.payment_date = data.payment_date;
                // Beraterabrechnung
                if (data.consultant_billed_date) cleanedData.consultant_billed_date = data.consultant_billed_date;
                if (data.consultant_billed_number) cleanedData.consultant_billed_number = data.consultant_billed_number;
                break;

            case 'temporary_consultant':
                // Temporäre Beraterdaten
                endpoint = 'update_temporary_invoice_data';
                if (data.temporary_billed_date) cleanedData.temporary_billed_date = data.temporary_billed_date;
                if (data.temporary_billed_number) cleanedData.temporary_billed_number = data.temporary_billed_number;
                break;

            default:
                throw new Error('Unbekannter Datentyp');
        }

        // Sende nur die tatsächlich vorhandenen Daten
        const response = await api.post(
            `${API_URL_LOANS}${loanId}/${endpoint}/`,
            cleanedData
        );
        return response.data;
    } catch (error) {
        console.error('Fehler beim Aktualisieren der Daten:', error);
        throw error;
    }
};

export const getConsultants = async () => {
    try {
        const response = await api.get(
            `${API_BASE_URL}/consultants/`
        );
        return response.data;
    } catch (error) {
        console.error('Fehler beim Laden der Berater:', error);
        throw error;
    }
};

export const getLoanById = async (loanId) => {
    try {
        const response = await api.get(`${API_URL_LOANS}${loanId}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching loan:', error);
        throw error;
    }
};

// Cache for detailed loan information
const loanDetailsCache = new Map();
const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

export const getDetailedLoanInfo = async (id, skipCache = false) => {
    const cacheKey = `loan_${id}`;
  
    // Skip cache if explicitly requested or if URL has timestamp parameter
    const urlParams = new URLSearchParams(window.location.search);
    const hasTimestamp = urlParams.has('ts');
  
    // Check cache first
    const cachedData = loanDetailsCache.get(cacheKey);
    if (!skipCache && !hasTimestamp && cachedData && (Date.now() - cachedData.timestamp < CACHE_EXPIRY_TIME)) {
        console.log('Returning cached loan details for:', id);
        return cachedData.data;
    }

    try {
        console.log('Fetching fresh loan details for:', id);
        const response = await api.get(`${API_URL_LOANS}${id}/`);

        // Cache the response
        loanDetailsCache.set(cacheKey, {
            data: response.data,
            timestamp: Date.now()
        });

        return response.data;
    } catch (error) {
        console.error('Error fetching loan details:', error);
        throw error;
    }
};

// Function to clear cache for a specific loan
export const clearLoanCache = (loanId) => {
    if (loanId) {
        loanDetailsCache.delete(loanId);
    } else {
        loanDetailsCache.clear();
    }
};

export const updateLoan = async (loanId, loanData) => {
    try {
        const response = await api.put(
            `${API_URL}loans/${loanId}/`,
            loanData
        );
        // Clear cache for this loan after update
        clearLoanCache(loanId);
        return response.data;
    } catch (error) {
        console.error('Fehler beim Aktualisieren des Vorgangs:', error);
        const errorMessage = error.response?.data?.errors 
            ? Object.values(error.response.data.errors).flat().join(', ')
            : error.response?.data?.error || 
              error.response?.data?.message ||
              'Fehler beim Aktualisieren des Vorgangs';
              
        throw new Error(errorMessage);
    }
};

export const fetchLoanDetails = async (id) => {
    try {
        const response = await getLoanById(id);
        return response;
    } catch (error) {
        console.error('Error fetching loan details:', error);
        throw error;
    }
};

export const updateLoanCommission = async (loanId, commissionData) => {
    try {
        const response = await api.put(
            `${API_URL}loans/${loanId}/commission/`,
            commissionData
        );
        return response.data;
    } catch (error) {
        console.error('Error updating loan commission:', error);
        throw error;
    }
};

export const getCustomerLoans = async (customerId) => {
    const response = await api.get(`${API_URL}customers/${customerId}/loans/`);
    return response.data;
};

export const updateConsultantBilling = async (loanId, data) => {
    try {
        const response = await api.patch(
            `${API_URL}loans/${loanId}/`,
            {
                consultant_billed_date: data.billingDate,
                consultant_billed_number: data.billingNumber
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error updating consultant billing:', error);
        throw error;
    }
};

export const updateTemporaryBilling = async (loanId, data) => {
    try {
        const response = await api.patch(
            `${API_URL}loans/${loanId}/`,
            {
                temporary_billed_date: data.billingDate,
                temporary_billed_number: data.billingNumber
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error updating temporary billing:', error);
        throw error;
    }
};

export const getWithdrawalPassedLoans = async () => {
    try {
        const response = await api.get(`${API_URL}withdrawal-passed/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching withdrawal passed loans:', error);
        return [];
    }
};

export const getPaymentConfirmedLoans = async () => {
    try {
        const response = await api.get(`${API_URL}payment-confirmed/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching payment confirmed loans:', error);
        return [];
    }
};

export const confirmPayments = async (loanIds, paymentDate) => {
    try {
        const response = await api.post(
            `${API_URL}payment-confirmed/`,
            {
                loan_ids: loanIds,
                payment_date: paymentDate
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error confirming payments:', error);
        throw error;
    }
};

export const downloadInvoicePDF = async (loanId) => {
  try {
    const response = await api({
      url: `${API_URL}loans/${loanId}/invoice-pdf/`,
      method: 'GET',
      responseType: 'blob', // Important for handling PDF files
    });

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1].replace(/"/g, '')
      : `invoice_${loanId}.pdf`;
    
    link.setAttribute('download', filename);
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    // Clean up the URL
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading invoice PDF:', error);
    throw error;
  }
};

export const getConsultantBillingOverview = async () => {
    try {
        const response = await api.get(`${API_URL}consultant-billing-overview/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching consultant billing overview:', error);
        throw error;
    }
};

export const previewBillingInvoice = async (billingData) => {
    try {
        // Format the data for the API
        const formattedData = {
            ...billingData,
            loans: billingData.loans.map(loan => ({
                ...loan,
                amount: parseFloat(loan.amount),
                provision: parseFloat(loan.provision),
                extra_bonus: parseFloat(loan.extra_bonus),
                payout_sum: parseFloat(loan.payout_sum),
                consultant_share: parseFloat(loan.consultant_share || 0)
            })),
            total_amount: parseFloat(billingData.total_amount),
            total_extra_bonus: parseFloat(billingData.total_extra_bonus),
            total_provision: parseFloat(billingData.total_provision),
            total_sum: parseFloat(billingData.total_provision+billingData.total_extra_bonus)
        };

        const response = await api.post(`${API_URL}pdf-generator/preview-billing-invoice/`, formattedData, {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
        
        return response.data.html;
    } catch (error) {
        console.error('Error generating invoice preview:', error);
        throw error;
    }
};

export const generateBillingInvoice = async (billingData) => {
    try {
        // Format the data for the API
        const formattedData = {
            ...billingData,
            loans: billingData.loans.map(loan => ({
                ...loan,
                amount: parseFloat(loan.amount),
                provision: parseFloat(loan.provision),
                extra_bonus: parseFloat(loan.extra_bonus),
                payout_sum: parseFloat(loan.payout_sum),
                consultant_share: parseFloat(loan.consultant_share || 0)
            })),
            total_amount: parseFloat(billingData.total_amount),
            total_extra_bonus: parseFloat(billingData.total_extra_bonus),
            total_provision: parseFloat(billingData.total_provision),
            total_sum: parseFloat(billingData.total_provision+billingData.total_extra_bonus)
        };

        const response = await api.post(`${API_URL}pdf-generator/billing-invoice/`, formattedData, {
            responseType: 'blob',
            headers: {
                'Accept': 'application/pdf, application/json',
                'Content-Type': 'application/json'
            }
        });

        // Check if the response is JSON (error) or PDF
        const contentType = response.headers['content-type'];
        if (contentType && contentType.includes('application/json')) {
            // Parse the JSON error
            const reader = new FileReader();
            const errorData = await new Promise((resolve, reject) => {
                reader.onload = () => resolve(JSON.parse(reader.result));
                reader.onerror = reject;
                reader.readAsText(response.data);
            });
            throw new Error(errorData.error || 'Error generating PDF');
        }
        
        // Handle PDF response
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        // Create a link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Rechnung_${billingData.billing_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return response.data;
    } catch (error) {
        console.error('Error generating billing invoice:', error);
        throw error;
    }
};

export const deleteLoan = async (id) => {
    try {
        const response = await api.delete(`/loans/${id}/`);
        return response.data;
    } catch (error) {
        console.error('Error deleting loan:', error);
        throw error;
    }
};

export const updateLoanStatus = async (id, status) => {
    try {
        console.log('updateLoanStatus called with:', { id, status });
        
        // Send the status without the prefix
        const statusValue = status.replace('LoanStatus.', '');
        const url = `${API_URL_LOANS}${id}/`;
        console.log('Making request to:', url, 'with data:', { status: statusValue });
        
        const response = await api.patch(url, { status: statusValue });
        console.log('Update status response:', response);
        
        clearLoanCache(id); // Clear cache after updating
        console.log('Cache cleared for loan:', id);
        
        return response.data;
    } catch (error) {
        console.error('Error updating loan status:', {
            error,
            response: error.response?.data,
            status: error.response?.status,
            url: error.config?.url,
            data: error.config?.data
        });
        throw error;
    }
};

// Consultant Billing Overview Methods
export const getConsultantBillingGroups = async () => {
    try {
        const response = await api.get(`${API_URL}consultant-billing-groups/`);
        return response;
    } catch (error) {
        console.error('Error fetching consultant billing groups:', error);
        throw error;
    }
};

export const previewConsultantBilling = async (billingNumber) => {
    try {
        const response = await api.get(`${API_URL}consultant-billing/${billingNumber}/preview/`);
        return response;
    } catch (error) {
        console.error('Error previewing consultant billing:', error);
        throw error;
    }
};

export const generateConsultantBillingPDF = async (billingNumber) => {
    try {
        const response = await api.get(`${API_URL}consultant-billing/${billingNumber}/pdf/`, {
            responseType: 'blob'
        });
        
        // Create a blob from the PDF stream
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        // Create a link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `consultant-billing-${billingNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        return response;
    } catch (error) {
        console.error('Error generating consultant billing PDF:', error);
        throw error;
    }
};